// @owners { team: patient-health }
// eslint-disable-next-line import/no-deprecated
import { getScreenSize } from '@alto/design-system';
// eslint-disable-next-line @alto/no-pocky-import
import { Column, FlexRow } from '@alto/pocky';
import autobind from 'autobind-decorator';
import React, { PureComponent } from 'react';
import styled from 'styled-components';
import CTAButton from './styles/button';
import { LogoCell, LogosGrid, ValuePropCell, ValuePropsGrid } from './styles/grid';
import { ContainedImage, HeadImage, Image } from './styles/image';
import { palette, spacing } from './styles/styles';
import {
  CheckBoxText,
  IntroText,
  LogosText,
  ValuePropDescription,
  ValuePropTitle,
  ValuePropsTitle,
} from './styles/text';
import ChecklistInfoRow from '~web/components/ChecklistInfoRow';
import logoAlto from '~web/images/altoLogo@2x.png';
import delivery from '~web/images/freeDelivery@3x.png';
import messaging from '~web/images/messaging@3x.png';
import oneMedical from '~web/images/oneMedical.png';
import pharmacist from '~web/images/pharmacistJennyGreen@3x.png';
import pricing from '~web/images/priceShopping@3x.png';
import stanford from '~web/images/stanford.png';
import ucsf from '~web/images/ucsf.png';
import urac from '~web/images/urac.png';
import vipps from '~web/images/vipps.png';

const ColoredFlexRow = styled(FlexRow).attrs({
  horizontallyAlignContent: true,
  verticallyAlignContent: true,
})<{ mSpacing?: string; mPadding?: string; color?: string; isSMScreenOrBigger: boolean }>`
  ${({ color }) => color && `background-color: ${color};`}

  ${({ isSMScreenOrBigger, mSpacing, mPadding }) =>
    !isSMScreenOrBigger &&
    `
      > *:not(:last-child) {
        margin-bottom: ${mSpacing || 0};
      }
      padding: ${mPadding || 0};
    `}
  }
`;

const LogoContainer = styled.a`
  width: 115px;
  height: 33px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LogosTextContainer = styled.div`
  max-width: 240px;
`;

type Props = {
  readonly handleOnboard: () => void;
};
export default class LandingPage extends PureComponent<Props> {
  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  renderHeader() {
    // eslint-disable-next-line import/no-deprecated
    const { isSMScreenOrBigger } = getScreenSize();
    return (
      <ColoredFlexRow
        color={palette.ivory}
        padding={spacing.lgSpacing}
        mPadding={spacing.smSpacing}
        isSMScreenOrBigger={isSMScreenOrBigger}
      >
        <LogoContainer
          href="https://alto.com"
          aria-label="Alto Logo"
        >
          <Image src={logoAlto} />
        </LogoContainer>
      </ColoredFlexRow>
    );
  }

  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  renderCTA() {
    const { handleOnboard } = this.props;
    // eslint-disable-next-line import/no-deprecated
    const { isSMScreenOrBigger } = getScreenSize();

    return (
      <ColoredFlexRow
        color={palette.ivory}
        spacing={spacing.lgSpacing}
        mSpacing={spacing.mdSpacing}
        padding={spacing.xxlSpacing}
        mPadding={`${spacing.mdSpacing} ${spacing.mdlgSpacing} ${spacing.mdlgSpacing}`}
        isSMScreenOrBigger={isSMScreenOrBigger}
      >
        <HeadImage
          src={pharmacist}
          alt="Headshot of friendly pharmacist"
          isSMScreenOrBigger={isSMScreenOrBigger}
        />
        <ColoredFlexRow
          spacing={spacing.mdSpacing}
          mSpacing={spacing.smSpacing}
          isSMScreenOrBigger={isSMScreenOrBigger}
        >
          <IntroText isSMScreenOrBigger={isSMScreenOrBigger}>
            Hi I’m Jenny, a pharmacist at Alto. We received your prescriptions and are ready to:
          </IntroText>
          <Column>
            <ChecklistInfoRow>
              <CheckBoxText isSMScreenOrBigger={isSMScreenOrBigger}>Find you a great price</CheckBoxText>
            </ChecklistInfoRow>
            <ChecklistInfoRow>
              <CheckBoxText isSMScreenOrBigger={isSMScreenOrBigger}>Deliver the same day for free!</CheckBoxText>
            </ChecklistInfoRow>
          </Column>
        </ColoredFlexRow>
        <CTAButton
          buttonText="Start Your Order"
          color={palette.citrus}
          hoverColor={palette.darkCitrus}
          onClick={handleOnboard}
        />
      </ColoredFlexRow>
    );
  }

  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  renderValueProps() {
    // eslint-disable-next-line import/no-deprecated
    const { isSMScreenOrBigger, isLGScreenOrBigger } = getScreenSize();

    return (
      <ColoredFlexRow
        color={palette.babyBlue}
        spacing={spacing.xlSpacing}
        mSpacing={spacing.mdlgSpacing}
        padding={spacing.xxlSpacing}
        mPadding={`${spacing.mdlgSpacing} ${spacing.lgSpacing} ${spacing.xlSpacing}`}
        isSMScreenOrBigger={isSMScreenOrBigger}
      >
        <ValuePropsTitle isSMScreenOrBigger={isSMScreenOrBigger}>How Alto Works</ValuePropsTitle>
        <ValuePropsGrid isLGScreenOrBigger={isLGScreenOrBigger}>
          <ValuePropCell isLGScreenOrBigger={isLGScreenOrBigger}>
            <ContainedImage
              src={pricing}
              alt="Receipt with copay and out of pocket amounts"
            />
            <ValuePropTitle isSMScreenOrBigger={isSMScreenOrBigger}>Committed to low prices</ValuePropTitle>
            <ValuePropDescription isSMScreenOrBigger={isSMScreenOrBigger}>
              Our care team works with your doctor and insurance to find you the best price.
            </ValuePropDescription>
          </ValuePropCell>
          <ValuePropCell isLGScreenOrBigger={isLGScreenOrBigger}>
            <ContainedImage
              src={messaging}
              alt="Hand holding a phone with messages"
            />
            <ValuePropTitle isSMScreenOrBigger={isSMScreenOrBigger}>Help at your fingertips</ValuePropTitle>
            <ValuePropDescription isSMScreenOrBigger={isSMScreenOrBigger}>
              Our pharmacists are available by phone or chat every day of the week.
            </ValuePropDescription>
          </ValuePropCell>
          <ValuePropCell isLGScreenOrBigger={isLGScreenOrBigger}>
            <ContainedImage
              src={delivery}
              alt="Man riding a bike with a gift box"
            />
            <ValuePropTitle isSMScreenOrBigger={isSMScreenOrBigger}>Free same-day delivery!</ValuePropTitle>
            <ValuePropDescription isSMScreenOrBigger={isSMScreenOrBigger}>
              Track our couriers in real-time as they safely deliver your medications.
            </ValuePropDescription>
          </ValuePropCell>
        </ValuePropsGrid>
      </ColoredFlexRow>
    );
  }

  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  renderLogos() {
    // eslint-disable-next-line import/no-deprecated
    const { isSMScreenOrBigger, isLGScreenOrBigger } = getScreenSize();

    return (
      <ColoredFlexRow
        color={palette.white}
        padding={spacing.smSpacing}
        isSMScreenOrBigger={isSMScreenOrBigger}
      >
        <LogosGrid isLGScreenOrBigger={isLGScreenOrBigger}>
          <LogoCell>
            <LogosTextContainer>
              <LogosText>
                Alto Pharmacy is accredited and trusted by many of the world’s best healthcare systems.
              </LogosText>
            </LogosTextContainer>
          </LogoCell>
          <LogoCell>
            <ContainedImage
              href="https://accreditnet2.urac.org//uracportal/Directory/CompanyView/8517"
              src={urac}
              alt="URAC Accreditation Seal"
            />
          </LogoCell>
          <LogoCell>
            <ContainedImage
              href="https://nabp.pharmacy/programs/vipps/vipps-accredited-pharmacies-list/"
              src={vipps}
              alt="VIPPS Accreditation Seal"
            />
          </LogoCell>
          <LogoCell>
            <ContainedImage
              src={oneMedical}
              alt="OneMedical logo"
            />
          </LogoCell>
          <LogoCell>
            <ContainedImage
              src={ucsf}
              alt="UCSF logo"
            />
          </LogoCell>
          <LogoCell>
            <ContainedImage
              src={stanford}
              alt="Stanford logo"
            />
          </LogoCell>
        </LogosGrid>
      </ColoredFlexRow>
    );
  }

  render() {
    return (
      <>
        {this.renderHeader()}
        {this.renderCTA()}
        {this.renderValueProps()}
        {this.renderLogos()}
      </>
    );
  }
}
